import {
  CheckSquare,
  CircleDot,
  FolderOpen,
  Grid3X3,
  GripHorizontal,
  ListOrdered,
  MessageSquare,
  Shuffle,
  SlidersHorizontal,
  Star,
  X,
} from "lucide-react-native";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Pressable,
  SectionList,
  SectionListData,
  SectionListRenderItemInfo,
  View,
} from "react-native";

import { Card, HStack, Skeleton, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question, Section } from "@/types/resources";

interface QuestionGroup {
  data: Question[];
  section: Section | null;
}

interface StudyMenuProps {
  activeSectionId: string;
  groupedData: QuestionGroup[];
  loading: boolean;
  onClose?: () => void;
  onItemPress: (sectionId: string) => void;
  /** When true, renders as a flush full-height panel (used inside the
      slide-in overlay) with a title bar and close button. */
  overlay?: boolean;
}

const getQuestionIcon = (type: string) => {
  switch (type) {
    case "linear_scale":
      return SlidersHorizontal;
    case "multi_select_matrix":
      return Grid3X3;
    case "multiple_choice":
      return CheckSquare;
    case "open_ended":
      return MessageSquare;
    case "rating_scale":
      return Star;
    case "category_assignment":
      return Shuffle;
    case "ranking":
      return ListOrdered;
    case "single_choice":
      return CircleDot;
    case "single_select_matrix":
      return GripHorizontal;
    default:
      return CircleDot;
  }
};

const ITEM_H = 48;

/* ── Skeleton ───────────────────────────────────────────── */

export const StudyMenuSkeleton: FC = () => (
  <View className="w-full lg:w-[320px] h-80 lg:h-full pb-4">
    <Card className="border border-outline-200 rounded-xl h-full overflow-hidden">
      <VStack className="py-3 px-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <VStack
            className="flex-row items-center px-3 gap-2"
            key={index}
            style={{ height: ITEM_H }}
          >
            <Skeleton className="h-4 w-4 rounded-md flex-shrink-0" />
            <Skeleton className="h-3 flex-1 rounded-md" />
          </VStack>
        ))}
      </VStack>
    </Card>
  </View>
);

/* ── Component ──────────────────────────────────────────── */

const StudyMenu: FC<StudyMenuProps> = ({
  activeSectionId,
  groupedData,
  loading,
  onClose,
  onItemPress,
  overlay = false,
}) => {
  const { i18n } = useLocalization();
  const sectionListRef = useRef<SectionList<Question, QuestionGroup>>(null);

  // Build index map for scrollToLocation
  const indexMap = useMemo(() => {
    const map: Record<string, { itemIndex: number; sectionIndex: number }> = {};
    groupedData.forEach((group, sectionIndex) => {
      if (group.section) {
        map[`section-${group.section.id}`] = { itemIndex: 0, sectionIndex };
      }
      group.data.forEach((question, itemIndex) => {
        map[question.id] = { itemIndex, sectionIndex };
      });
    });
    return map;
  }, [groupedData]);

  // Tracks the last requested target so we can retry if the row isn't
  // measured yet (SectionList throws otherwise — see onScrollToIndexFailed).
  const pendingScrollId = useRef<string | null>(null);

  // Scroll to active item
  const scrollToActiveItem = useCallback(
    (id: string) => {
      const location = indexMap[id];
      if (location && sectionListRef.current) {
        pendingScrollId.current = id;
        sectionListRef.current.scrollToLocation({
          ...location,
          itemIndex: location.itemIndex + 1,
          animated: true,
          viewOffset: 150,
        });
      }
    },
    [indexMap]
  );

  // Rows have no fixed getItemLayout, so scrolling to an offscreen item can
  // fail until it's measured. Retry once after layout settles.
  const handleScrollToIndexFailed = useCallback(() => {
    const id = pendingScrollId.current;
    if (!id) return;
    setTimeout(() => {
      const location = indexMap[id];
      if (location && sectionListRef.current) {
        sectionListRef.current.scrollToLocation({
          ...location,
          itemIndex: location.itemIndex + 1,
          animated: true,
          viewOffset: 150,
        });
      }
    }, 200);
  }, [indexMap]);

  // Debounce the scroll-to-active by 500ms via the effect's own timer so we
  // don't build a debounced wrapper around a ref-reading function during render.
  useEffect(() => {
    if (!activeSectionId) return;
    const timeout = setTimeout(() => scrollToActiveItem(activeSectionId), 500);
    return () => clearTimeout(timeout);
  }, [activeSectionId, scrollToActiveItem]);

  const renderItem = useCallback(
    ({ item, section }: SectionListRenderItemInfo<Question, QuestionGroup>) => {
      const isActive = activeSectionId === item.id;
      const Icon = getQuestionIcon(item.type);

      return (
        <Pressable
          className={`flex-row items-center px-3 ${
            section.section ? "ml-3 pl-2 border-l border-outline-200" : ""
          }`}
          onPress={() => onItemPress(item.id)}
          style={{ height: ITEM_H }}
        >
          <Icon
            className={`flex-shrink-0 ${
              isActive ? "text-primary-500" : "text-typography-600"
            }`}
            size={16}
          />
          <Text
            className={`ml-2 text-xs font-medium line-clamp-2 ${
              isActive ? "text-primary-500" : "text-typography-700"
            }`}
          >
            {item.type === "category_assignment"
              ? i18n.t("questions.categoryAssignment.title")
              : item.text}
          </Text>
        </Pressable>
      );
    },
    [activeSectionId, i18n, onItemPress]
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: SectionListData<Question, QuestionGroup> }) => {
      const groupIndex = groupedData.indexOf(
        section as unknown as QuestionGroup
      );
      const isFirst = groupIndex <= 0;
      const prevGroup = isFirst ? null : groupedData[groupIndex - 1];
      const needsSpacing = !isFirst && (section.section || prevGroup?.section);

      if (!section.section) {
        if (!needsSpacing) return null;
        return <View style={{ height: 16 }} />;
      }

      const sectionId = `section-${section.section.id}`;
      const isActive = activeSectionId === sectionId;

      return (
        <Pressable
          className={`flex-row items-center px-3${needsSpacing ? " mt-4" : ""}`}
          onPress={() => onItemPress(sectionId)}
          style={{ height: ITEM_H }}
        >
          <FolderOpen
            className={`flex-shrink-0 ${
              isActive ? "text-primary-500" : "text-typography-600"
            }`}
            size={16}
          />
          <Text
            className={`ml-2 text-sm font-semibold line-clamp-2 ${
              isActive ? "text-primary-500" : "text-typography-800"
            }`}
          >
            {section.section.title}
          </Text>
        </Pressable>
      );
    },
    [activeSectionId, groupedData, onItemPress]
  );

  const keyExtractor = useCallback((item: Question) => item.id, []);

  const renderFooter = useCallback(
    () =>
      loading ? (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <VStack
              className="flex-row items-center px-3 gap-2"
              key={index}
              style={{ height: ITEM_H }}
            >
              <Skeleton className="h-4 w-4 rounded-md flex-shrink-0" />
              <Skeleton className="h-3 flex-1 rounded-md" />
            </VStack>
          ))}
        </>
      ) : null,
    [loading]
  );

  return (
    <View
      className={
        overlay ? "w-full h-full" : "w-full lg:w-[320px] h-80 lg:h-full pb-4"
      }
    >
      <Card
        className={`border-outline-200 h-full overflow-hidden ${
          overlay
            ? "border-l rounded-none bg-background-0"
            : "border rounded-xl"
        }`}
      >
        {overlay && (
          <HStack className="-mx-4 items-center justify-between gap-2 px-4 pt-3 pb-5 border-b border-outline-200">
            <Text className="flex-1 text-lg font-semibold text-typography-900">
              {i18n.t("dashboardNavigator.questions")}
            </Text>
            {onClose && (
              <Pressable className="shrink-0 p-1" onPress={onClose}>
                <X color="rgb(var(--color-typography-500))" size={18} />
              </Pressable>
            )}
          </HStack>
        )}
        <SectionList
          ref={sectionListRef}
          className="flex-1 mt-5"
          contentContainerClassName="py-3 px-2"
          extraData={activeSectionId}
          keyExtractor={keyExtractor}
          ListFooterComponent={renderFooter}
          onScrollToIndexFailed={handleScrollToIndexFailed}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          sections={groupedData}
          showsVerticalScrollIndicator={false}
          stickySectionHeadersEnabled={false}
        />
      </Card>
    </View>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default StudyMenu;
